import * as React from "react";
import { Icon, MinimalButton, Position, ScrollMode, SpecialZoomLevel, Tooltip, Viewer, Worker } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';

import disableScrollPlugin from './disableScrollPlugin';

import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { useMediaQuery } from 'usehooks-ts';

import Logo from '../assets/images/logo-small.svg';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';

import './PDFViewer.css';

export const PDFViewer = ({
    url,
    hideWaterMark = false,
    fullScreen = false,
    toolbar = true,
    scrollVertically = true,
    slides = false,
    print = false
}) => {
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar, renderDefaultToolbar } = toolbarPluginInstance;

    const screen479 = useMediaQuery('(max-width: 479px)');

    const appendPdfConfigToUrl = url => {
        return `${url}#toolbar=0&navpanes=0&zoom=${screen479 ? '35' : '90'}`;
    }

    const renderError = () => (
        <iframe
            src={appendPdfConfigToUrl(url)}
            frameBorder="0"
            scrolling="auto"
            height={screen479 ? '500px' : '1050px'}
            width={screen479 ? '330px' : '820px'}
            className="document-preview"
        ></iframe>
    );

    const transform = slot => ({
        ...slot,
        // These slots will be empty
        Download: () => <></>,
        DownloadMenuItem: () => <></>,
        SwitchTheme: () => <></>,
        SwitchThemeMenuItem: () => <></>,
        ...(!print && { Print: () => <></> }),
        PrintMenuItem: () => <></>,
        Open: () => <></>,
        OpenMenuItem: () => <></>,
        EnterFullScreen: () => <></>,
        EnterFullScreenMenuItem: () => <></>
    });

    const renderPage = (props) => (
        <>
            {props.canvasLayer.children}
            {props.annotationLayer.children}
            {props.textLayer.children}
            {!hideWaterMark && (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            alignItems: 'center',
                            color: 'rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            fontSize: `${2 * props.scale}rem`,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            transform: 'rotate(-45deg)',
                            userSelect: 'none',
                        }}
                    >
                        <img src={Logo} width="80" alt="Logo" className="watermark-image" />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.5.207/build/pdf.worker.js">
            <div
                style={{
                    width: '100%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    maxWidth: '100%',
                    paddingBottom: '1rem',
                    aspectRatio: '81/107'
                }}
            >
                <div
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#eeeeee',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        padding: '4px'
                    }}
                >
                    {toolbar && (
                        <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
                    )}
                </div>
                <Viewer
                    fileUrl={url}
                    renderPage={renderPage}
                    httpHeaders={{
                        "AllowedHeaders": ["*"],
                        "AllowedMethods": ["GET", "POST"],
                        "AllowedOrigins": ["*"],
                    }}
                    plugins={[toolbarPluginInstance]}
                    renderError={renderError}
                    scrollMode={scrollVertically ? ScrollMode.Vertical : ScrollMode.Horizontal}
                    defaultScale={SpecialZoomLevel.PageFit}
                />
            </div>
        </Worker>
    )
};

export const PDFSlideViewer = ({
    url,
    hideWaterMark = false,
    fullScreen = false,
}) => {
    const disableScrollPluginInstance = disableScrollPlugin();
    const pageNavigationPluginInstance = pageNavigationPlugin();

    const { GoToNextPage, GoToPreviousPage, CurrentPageLabel } = pageNavigationPluginInstance;


    const screen479 = useMediaQuery('(max-width: 479px)');

    const appendPdfConfigToUrl = url => {
        return `${url}#toolbar=0&navpanes=0&zoom=${screen479 ? '35' : '90'}`;
    }

    const renderError = () => (
        <iframe
            src={appendPdfConfigToUrl(url)}
            frameBorder="0"
            scrolling="auto"
            height={screen479 ? '500px' : '1050px'}
            width={screen479 ? '330px' : '820px'}
            className="document-preview"
        ></iframe>
    );

    const renderPage = (props) => (
        <>
            {props.canvasLayer.children}
            {props.annotationLayer.children}
            {props.textLayer.children}
            {!hideWaterMark && (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            alignItems: 'center',
                            color: 'rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            fontSize: `${2 * props.scale}rem`,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            transform: 'rotate(-45deg)',
                            userSelect: 'none',
                        }}
                    >
                        <img src={Logo} width="80" alt="Logo" className="watermark-image" />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <Worker style={{
            width: '100%'
        }}
            workerUrl="https://unpkg.com/pdfjs-dist@2.5.207/build/pdf.worker.js"
        >
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                paddingBottom: '1rem',
                flexDirection: 'column'
            }}>
                {!screen479 && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%'
                        }}
                    >
                        <div>
                            <GoToPreviousPage>
                                {props => (
                                    <Tooltip
                                        position={Position.BottomCenter}
                                        target={
                                            <MinimalButton onClick={props.onClick}>
                                                <Icon size={22}>
                                                    <path strokeWidth="3" d="M18.4.5,5.825,11.626a.5.5,0,0,0,0,.748L18.4,23.5" />
                                                </Icon>
                                            </MinimalButton>
                                        }
                                        content={() => 'Previous page'}
                                        offset={{ left: 0, top: 8 }}
                                    />
                                )}
                            </GoToPreviousPage>
                        </div>
                        <CurrentPageLabel>
                            {props => (
                                <span>Page {props.currentPage + 1} of {props.numberOfPages}</span>
                            )}
                        </CurrentPageLabel>
                        <div>
                            <GoToNextPage>
                                {(props) => (
                                    <Tooltip
                                        position={Position.BottomCenter}
                                        target={
                                            <MinimalButton onClick={props.onClick}>
                                                <Icon size={22}>
                                                    <path strokeWidth="3" d="M5.651,23.5,18.227,12.374a.5.5,0,0,0,0-.748L5.651.5" />
                                                </Icon>
                                            </MinimalButton>
                                        }
                                        content={() => 'Next page'}
                                        offset={{ left: 0, top: 8 }}
                                    />
                                )}
                            </GoToNextPage>
                        </div>
                    </div>
                )}
                <div
                    className="rpv-core__viewer"
                    style={{
                        border: '1px solid rgba(0, 0, 0, 0.3)',
                        aspectRatio: '16/9',
                        width: '100%',
                    }}
                >
                    <Viewer
                        fileUrl={url}
                        renderPage={renderPage}
                        httpHeaders={{
                            "AllowedHeaders": ["*"],
                            "AllowedMethods": ["GET", "POST"],
                            "AllowedOrigins": ["*"],
                        }}
                        renderError={renderError}
                        plugins={[disableScrollPluginInstance, pageNavigationPluginInstance]}
                        defaultScale={SpecialZoomLevel.PageWidth}
                        scrollMode={!screen479 ? ScrollMode.Horizontal : ScrollMode.Vertical}
                    />
                </div>
            </div>
        </Worker>
    )
};